import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ExternalLink, Clock, Users, CalendarPlus, Star } from "lucide-react";
import { EventLogo } from "./EventLogo";
import { fmtRange } from "../../lib/events";
import { BACKEND_URL } from "../../lib/backend";

const ACCESS_STYLE = {
  free: "bg-emerald-500/12 text-emerald-600",
  paid: "bg-amber-500/12 text-amber-600",
  invite: "bg-violet-500/12 text-violet-600",
  not_specified: "bg-slate-500/12 text-slate-500",
};

export function Chip({ children, className = "", testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${className}`}>
      {children}
    </span>
  );
}

export function EventCard({ ev, dm, sameWeek = 0, compact = false }) {
  const c = dm
    ? { card: "bg-[#1a2324] border-slate-700/50", text: "text-slate-200", muted: "text-slate-500", accent: "text-cyan-400", chip: "bg-white/6 text-slate-300" }
    : { card: "bg-white border-[#e7e8e9]", text: "text-[#191c1d]", muted: "text-[#6b7a7d]", accent: "text-[#006874]", chip: "bg-[#f3f4f5] text-[#3b494c]" };
  const where = [ev.city, ev.country].filter(Boolean).join(", ");
  return (
    <article data-testid={`event-card-${ev.id}`} className={`rounded-2xl border p-4 sm:p-5 transition-shadow hover:shadow-md ${c.card}`}>
      <div className="flex items-start gap-4">
        <Link to={`/events/${ev.slug}`} className="flex-shrink-0" aria-label={ev.name}><EventLogo event={ev} dm={dm} size={compact ? 40 : 52} /></Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-xs font-semibold tabular-nums ${c.accent}`} data-testid={`event-dates-${ev.id}`}>{fmtRange(ev.start_date, ev.end_date)}</p>
            {ev.ongoing && <Chip className="bg-emerald-500/15 text-emerald-500" testid={`event-now-badge-${ev.id}`}>Happening now</Chip>}
            {ev.covered_by_ti && <Chip className={dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"} testid={`event-covered-${ev.id}`}><Star className="w-2.5 h-2.5" /> Covered by TourismIntel</Chip>}
          </div>
          <h3 className={`mt-1 text-base font-semibold leading-snug ${c.text}`}>
            <Link to={`/events/${ev.slug}`} className="hover:underline" data-testid={`event-title-link-${ev.id}`}>{ev.name}</Link>
          </h3>
          <p className={`mt-1 text-xs flex items-center gap-1 flex-wrap ${c.muted}`}>
            {where && <><MapPin className="w-3 h-3 flex-shrink-0" />{where}</>}
            {ev.venue && <span className="opacity-80">· {ev.venue}</span>}
            {ev.organiser && <span className="opacity-80">· by {ev.organiser}</span>}
          </p>
          <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
            <Chip className={c.chip} testid={`event-type-${ev.id}`}>{ev.event_type_label}</Chip>
            {ev.format_label && <Chip className={c.chip}>{ev.format_label}</Chip>}
            <Chip className={ACCESS_STYLE[ev.access] || ACCESS_STYLE.not_specified} testid={`event-access-${ev.id}`}>{ev.access_label}</Chip>
            {ev.closes_in_days !== null && ev.closes_in_days !== undefined && ev.closes_in_days <= 60 && (
              <Chip className="bg-rose-500/12 text-rose-500" testid={`event-closing-${ev.id}`}><Clock className="w-2.5 h-2.5" /> Registration closes in {ev.closes_in_days}d</Chip>
            )}
            {ev.attendance?.value && <Chip className={c.chip} testid={`event-attendance-${ev.id}`}><Users className="w-2.5 h-2.5" /> {ev.attendance.value}</Chip>}
            {sameWeek > 0 && <Chip className={c.chip} testid={`event-sameweek-${ev.id}`}>+{sameWeek} the same week</Chip>}
          </div>
          {!compact && ev.why_it_matters && (
            <p className={`mt-2.5 text-[13px] leading-relaxed line-clamp-2 ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>{ev.why_it_matters}</p>
          )}
          <div className="mt-3 flex items-center gap-3 text-xs">
            <Link to={`/events/${ev.slug}`} className={`font-semibold hover:underline ${c.accent}`} data-testid={`event-detail-link-${ev.id}`}>Event page →</Link>
            {ev.official_url && (
              <a href={ev.official_url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 hover:underline ${c.muted}`} data-testid={`event-link-${ev.id}`}>
                Official site <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <a href={`${BACKEND_URL}/api/events/${ev.slug}.ics`} className={`inline-flex items-center gap-1 hover:underline ${c.muted}`} data-testid={`event-ics-${ev.id}`} title="Add to calendar (.ics)">
              <CalendarPlus className="w-3 h-3" /> .ics
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

export default EventCard;
